import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';
import axios from 'axios';
import FAQModal from './FAQModal';
import LegalModal from './LegalModal';
import BrandMark from '../BrandMark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to ensure external URLs have https:// prefix
const ensureAbsoluteUrl = (url) => {
  if (!url) return '';
  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed}`;
};

const Footer = ({ onContactClick }) => {
  const currentYear = new Date().getFullYear();
  const [socialLinks, setSocialLinks] = useState({
    linkedin: '',
    youtube: '',
    instagram: '',
  });
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalType, setLegalType] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data.social_links) {
          setSocialLinks(response.data.social_links);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };

    fetchSettings();
  }, []);

  const footerLinks = {
    product: [
      { label: 'Features', href: '#features' },
      { label: 'How It Works', href: '#how-it-works' },
      { label: 'FAQ', href: '#faq', isFAQ: true },
      { label: 'Knowledge', href: '/knowledge', isKnowledge: true },
    ],
    company: [
      { label: 'About Us', href: '/about', isAbout: true },
      { label: 'Partner with us', href: '#contact', isContact: true },
    ],
    legal: [
      { label: 'Privacy Policy', href: '#privacy', isLegal: true, legalType: 'privacy' },
      { label: 'Terms of Service', href: '#terms', isLegal: true, legalType: 'terms' },
    ],
  };

  const handleLegalClick = (type) => {
    setLegalType(type);
    setShowLegalModal(true);
  };

  const closeLegalModal = () => {
    setShowLegalModal(false);
    setLegalType(null);
  };

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLinkClick = (link) => {
    if (link.isContact && onContactClick) {
      onContactClick();
    } else {
      scrollToSection(link.href);
    }
  };

  return (
    <footer className="bg-slate-950 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <a href="/" className="flex items-center space-x-2 mb-6">
              <BrandMark size="md" textSize="text-2xl" />
            </a>
            <p className="text-slate-400 mb-6 max-w-sm leading-relaxed">
              Secure your legacy with AI-powered will creation. Protect what matters most with ease and confidence.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a href="mailto:help@willkart.ai" className="flex items-center gap-3 text-slate-400 hover:text-amber-400 transition-colors">
                <Mail className="w-4 h-4" />
                <span className="text-sm">help@willkart.ai</span>
              </a>
              <div className="flex items-center gap-3 text-slate-400">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">Mumbai, India</span>
              </div>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Product</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  {link.isKnowledge ? (
                    <a
                      href={link.href}
                      className="text-slate-400 hover:text-amber-400 transition-colors text-sm"
                      data-testid="footer-knowledge-link"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <button
                      onClick={() => link.isFAQ ? setShowFAQModal(true) : scrollToSection(link.href)}
                      className="text-slate-400 hover:text-amber-400 transition-colors text-sm"
                    >
                      {link.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  {link.isAbout ? (
                    <Link
                      to={link.href}
                      className="text-slate-400 hover:text-amber-400 transition-colors text-sm"
                      data-testid="footer-about-link"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleLinkClick(link)}
                      className="text-slate-400 hover:text-amber-400 transition-colors text-sm"
                    >
                      {link.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => link.isLegal ? handleLegalClick(link.legalType) : scrollToSection(link.href)}
                    className="text-slate-400 hover:text-amber-400 transition-colors text-sm"
                    data-testid={`footer-${link.legalType}-link`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © {currentYear} WillKart.ai. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {socialLinks.linkedin && (
              <a
                href={ensureAbsoluteUrl(socialLinks.linkedin)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-amber-400 transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
                </svg>
              </a>
            )}
            {socialLinks.youtube && (
              <a
                href={ensureAbsoluteUrl(socialLinks.youtube)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-amber-400 transition-colors"
                aria-label="YouTube"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>
            )}
            {socialLinks.instagram && (
              <a
                href={ensureAbsoluteUrl(socialLinks.instagram)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-amber-400 transition-colors"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                </svg>
              </a>
            )}
            {/* Show default icons if no social links are set */}
            {!socialLinks.linkedin && !socialLinks.youtube && !socialLinks.instagram && (
              <>
                <span className="text-slate-600 hover:text-slate-500 transition-colors cursor-not-allowed" aria-label="LinkedIn">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
                  </svg>
                </span>
                <span className="text-slate-600 hover:text-slate-500 transition-colors cursor-not-allowed" aria-label="YouTube">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </span>
                <span className="text-slate-600 hover:text-slate-500 transition-colors cursor-not-allowed" aria-label="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* FAQ Modal */}
      <FAQModal isOpen={showFAQModal} onClose={() => setShowFAQModal(false)} />
      
      {/* Legal Modal */}
      <LegalModal isOpen={showLegalModal} onClose={closeLegalModal} type={legalType} />
    </footer>
  );
};

export default Footer;
